<template>
  <div class="consolidation-cockpit-page mt-5">
    
    <!-- Fullscreen Loader -->
    <div v-if="isLoading" class="pageloader is-active"><span class="title">Loading...</span></div>

     <nav class="breadcrumb mt-5" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
        <li class="is-active"><router-link to="/dashboard/consolidate-invoice" aria-current="true">Consolidation Cockpit</router-link></li>
      </ul>
    </nav>

    <!-- Month Tabs -->
    <div class="tabs is-toggle is-small mt-4">
      <ul>
        <li :class="{ 'is-active': currentTab === 'current' }">
          <a @click="switchTab('current')">Current Month</a>
        </li>
        <li :class="{ 'is-active': currentTab === 'previous' }">
          <a @click="switchTab('previous')">Previous Month</a>
        </li>
      </ul>
    </div>

    <!-- Consolidate All Button -->
    <!-- <div class="mb-4 pl-4">
      <button
        class="button is-primary is-small"
        :disabled="disableConsolidate"
        @click="confirmConsolidate"
      >
        Consolidate All {{ currentTab === 'current' ? 'Current' : 'Previous' }} Month
      </button>
    </div> -->

    <div class="column is-12 mb-4" style="padding-left: 20px;">
      <div class="columns is-vcentered" style="gap: 15px; margin-top: 10px;">
        <input v-model="filters.invoice_number" type="text" class="input is-rounded is-small" placeholder="Invoice Number">
        <input v-model="filters.agent_code" type="text" class="input is-rounded is-small" placeholder="Agent Code">
        <input v-model="filters.contract_number" type="text" class="input is-rounded is-small" placeholder="Contract Number">
      </div>

      <!-- Multiselect Filter Fields Row -->
      <div class="columns is-vcentered" style="gap: 15px; margin-top: 10px;">
        <div style="width: 500px;">
          <multiselect 
            v-model="filters.invoice_type" 
            :options="uniqueInvoiceTypes || []" 
            :multiple="true" 
            placeholder="Select Invoice Type" 
            :close-on-select="false"
            label="description"   
            track-by="code">         
          </multiselect>
        </div>
        
        <div style="width: 500px;">
          <multiselect 
            v-model="filters.sender_store_code" 
            :options="storeCodes || []" 
            :multiple="true" 
            placeholder="Select Department" 
            :close-on-select="false"
            track-by="id"
            label="code">
          </multiselect>
        </div>
      </div>
    
      <div class="columns is-vcentered" style="gap:15px; margin-top:10px;">
        <!-- Status Message text search -->
        <div class="control" style="width:300px;">
          <input
            v-model="filters.status_message"
            type="text"
            class="input is-rounded is-small"
            placeholder="Filter by Status Message"
          />
        </div>

        <!-- Opt-In/Opt-Out selector -->
        <div class="control">
          <div class="select is-small">
            <select v-model="filters.opt_status">
              <option value="">All Opt Statuses</option>
              <option value="in">Only Opted-In</option>
              <option value="out">Only Opted-Out</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="level">
      <div class="level-left">
        <div class="field is-grouped">
          <div class="control">
            <button class="button is-small is-success" @click="applyFilters">
              Apply Filters
            </button>
          </div>
          <div class="control">
            <button class="button is-small is-danger" @click="clearFilters">
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      <div class="level-right">
        <div class="field has-addons">
          <!-- Bulk Opt-In: show unless we're already in “only opted-in” view -->
          <div
            class="control mr-2"
            v-if="filters.opt_status !== 'in'"
          >
            <button
              class="button is-small is-info"
              @click="bulkUpdate('opt_in')"
              :disabled="!selectedInvoiceIds.length"
            >
              Bulk Opt-In
            </button>
          </div>

          <!-- Bulk Opt-Out: show unless we're already in “only opted-out” view -->
          <div
            class="control"
            v-if="filters.opt_status !== 'out'"
          >
            <button
              class="button is-small is-warning"
              @click="bulkUpdate('opt_out')"
              :disabled="!selectedInvoiceIds.length"
            >
              Bulk Opt-Out
            </button>
          </div>

          <!-- Bulk Opt-Out: show unless we're already in “only opted-out” view -->
          <div class="control ml-2" v-if="showBulkSubmit">
            <button
              class="button is-small is-primary"
              @click="bulkSubmit"
              :disabled="!selectedInvoiceIds.length"
            >
              Bulk Submit
            </button>
          </div>

          <!-- Page-size selector -->
          <div class="control ml-3">
            <div class="select is-small">
              <select v-model.number="pageSize" @change="onPageSizeChange">
                <option
                  v-for="size in pageSizes"
                  :key="size"
                  :value="size"
                >
                  {{ size }} per page
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Invoice Table -->
    <div class="column is-12">
      <table class="table is-fullwidth is-size-7">
        <thead class="is-size-7">
          <tr>
            <th style="width: 50px;">
              <input type="checkbox" @change="toggleSelectAll" :checked="allSelected" />
            </th>
            <th>Department</th>
            <th>Invoice Number</th>
            <th>Client Name</th>
            <th>Client ID</th>
            <th>Invoice Type</th>
            <th>Invoice Date</th>
            <th>Status</th>
            <th>Message</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody class="is-size-7">
          <tr v-for="invoice in invoices" :key="invoice.uuid">
            <td>
              <input type="checkbox" :value="invoice.uuid" v-model="selectedInvoiceIds" />
            </td>
            <td>{{ invoice.sender_store_code }}</td>
            <td>{{ invoice.invoice_number }}</td>
            <td>{{ invoice.client_name ? invoice.client_name : '' }}</td>
            <td>{{ invoice.client_id ? invoice.client_id : '' }}</td>
            <td>{{ invoice.invoice_type_description }}</td>
            <td>{{ invoice.invoice_date }}</td>
            <td>{{ invoice.status }}</td>
            <td>{{ invoice.status_message }}</td>
            <td>
              <button
                v-if="invoice.post_processing_flag === 'DONT_CONSOLIDATE'"
                class="button is-small is-info"
                @click="updateInvoiceFlag(invoice.uuid, 'opt_in')"
              >
                Opt-In
              </button>
              <button
                v-else
                class="button is-small is-warning"
                @click="updateInvoiceFlag(invoice.uuid, 'opt_out')"
              >
                Opt-Out
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="columns is-vcentered">
      <div class="column">
        <nav class="pagination is-centered is-small" role="navigation" aria-label="pagination">
          <a class="pagination-previous is-small" :class="{ 'is-disabled': currentPage === 1 }" @click="goToPage(currentPage - 1)">Previous</a>
          <a class="pagination-next is-small" :class="{ 'is-disabled': currentPage === totalPages || totalPages === 0 }" @click="goToPage(currentPage + 1)">Next</a>
          <ul class="pagination-list">
            <li v-for="page in totalPages" :key="page">
              <a class="pagination-link is-small" :class="{ 'is-current': page === currentPage }" @click="goToPage(page)">{{ page }}</a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { toast } from 'bulma-toast';
import Multiselect from 'vue-multiselect';
import { mapState } from 'vuex';

export default {
  name: 'ConsolidationCockpit',
  components: {
    Multiselect
  },
  data() {
    return {
      isLoading: false,
      invoices: [],
      currentTab: 'current', 
      currentPage: 1,
      pageSize: 50,
      pageSizes: [50, 100, 200],
      totalItems: 0,
      selectedInvoiceIds: [],
      showBulkSubmit: false,
      filters: {
        invoice_number: '',
        agent_code: '',
        contract_number: '',
        invoice_type: [],
        sender_store_code: [],
        document_type: '',
        status_message: '',
        opt_status: '',    // '' | 'opted_in' | 'opted_out'
      },
    };
  },
  computed: {
    ...mapState({
      storeCodes: state => state.user?.store_code || []
    }),
    disableConsolidate() {
      // disable previous-month consolidate if >7 days into current month
      if (this.currentTab === 'previous') {
        const today = new Date();
        return today.getDate() > 7;
      }
      return false;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.pageSize);
    },
    allSelected() {
      return (
        this.invoices.length > 0 &&
        this.invoices.every(invoice => this.selectedInvoiceIds.includes(invoice.uuid))
      );
    },
  },
  methods: {
    onGlobalKeydown(e) {
    // Ctrl+B or ⌘+B
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        this.showBulkSubmit = !this.showBulkSubmit;
      }
    },
    confirmConsolidate() {
      const monthLabel = this.currentTab === 'current' ? 'current month' : 'previous month';
      const message = `This will consolidate all invoices for the ${monthLabel}. This action cannot be undone. Continue?`;
      if (!window.confirm(message)) return;
      this.doConsolidate();
    },
    doConsolidate() {
      this.isLoading = true;
      axios.post('/api/v1/consolidation-cockpit/consolidate/', { option: this.currentTab })
        .then(res => {
          const nums = (res.data || []).map(inv => inv.consolidate_invoice_number);
          window.alert(`Created consolidated invoices:\n${nums.join('\n')}`);
          this.fetchInvoices();
        })
        .catch(() => {
          toast({ message: 'Error consolidating invoices', type: 'is-danger' });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async bulkSubmit() {
      if (!this.selectedInvoiceIds.length) return;

      this.isLoading = true;
      try {
        const res = await axios.post('/api/v1/integration/retrigger-submission/', {
          uuids: this.selectedInvoiceIds
        });
        toast({
          message: res.data.detail || 'Invoices enqueued for submission!',
          type: 'is-success'
        });
        // clear selection if you like
        this.selectedInvoiceIds = [];
      } catch (err) {
        console.error('Bulk submit error:', err);
        toast({
          message: 'Error enqueuing bulk submit. Please try again.',
          type: 'is-danger'
        });
      } finally {
        this.isLoading = false;
      }
    },
    switchTab(tab) {
      this.currentTab = tab
      this.currentPage = 1
      this.clearFilters();
      this.selectedInvoiceIds = [];
      this.fetchInvoices()
    },
    getUniqueTypes() {
      axios
        .get('/api/v1/unique-types/')
        .then(response => {
          this.uniqueInvoiceTypes = response.data.invoice_types || [];
          this.uniqueDocumentTypes = response.data.document_types || [];
        })
        .catch(error => {
          console.error('Error fetching unique types:', error);
          this.uniqueInvoiceTypes = [];
          this.uniqueDocumentTypes = [];
        });
    },
    fetchInvoices() {

      this.isLoading = true;
      // calculate dateStart/dateEnd based on tab
      const today = new Date()
      let start, end

      if (this.currentTab === 'current') {
        start = new Date(today.getFullYear(), today.getMonth(), 1)
        end   = today
      } else {
        // previous month: first day…
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        // …to last day of previous month:
        end   = new Date(today.getFullYear(), today.getMonth(), 0)
      }

      const fmt = (d) => d.toISOString().split('T')[0]

      const params = new URLSearchParams({
        dateStart: fmt(start),
        dateEnd: fmt(end),
        page: this.currentPage,
        page_size: this.pageSize
      })

      if (this.filters.invoice_number) params.append('invoice_number', this.filters.invoice_number);

      if (this.filters.status_message) {
        params.append('status_message', this.filters.status_message);
      }

      // new opt-status filter:
      if (this.filters.opt_status === 'in') {
        // exclude DONT_CONSOLIDATE
        params.append('opt_status', 'in');
      } else if (this.filters.opt_status === 'out') {
        // only DONT_CONSOLIDATE
        params.append('opt_status', 'out');
      }

      // Use a fallback empty array to avoid calling .length on undefined
      if ((this.filters.invoice_type || []).length) {
        const codes = this.filters.invoice_type.map(item => item.code); // 👈 extract only codes
        params.append('invoice_type', codes.join(','));
      }

      if ((this.filters.sender_store_code || []).length) {
        const codes = this.filters.sender_store_code.map(item => item.code);
        params.append('sender_store_code', codes.join(','));
      }
      if (this.filters.document_type) params.append('document_type', this.filters.document_type);
      if (this.filters.agent_code) params.append('agent_code', this.filters.agent_code);
      if (this.filters.contract_number) params.append('contract_number', this.filters.contract_number);


      axios.get('/api/v1/consolidation-cockpit/', {params})
        .then(res => {
          this.invoices = res.data.results;
          this.totalItems = res.data.count;
        })
        .catch(() => {
          toast({ message: 'Error loading invoices', type: 'is-danger' });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchInvoices();
    },
    clearFilters() {
      this.filters = {
        invoice_number: '',
        agent_code: '',
        contract_number: '',
        invoice_type: [],
        sender_store_code: [],
        document_type: '',
        status_message: '',
        opt_status: '', 
      };
      this.applyFilters();
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedInvoiceIds = [];
      } else {
        this.selectedInvoiceIds = this.invoices.map(i => i.uuid);
      }
    },
    goToPage(page) {
      this.currentPage = page;
      this.fetchInvoices();
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.fetchInvoices();
    },
    updateInvoiceFlag(uuid, action) {
      axios
        .post('/api/v1/consolidation-cockpit/update/', {
          uuids: [uuid],
          action: action
        })
        .then(() => {
          toast({ message: 'Invoice updated', type: 'is-success' });
          this.fetchInvoices();
        });
    },
    bulkUpdate(action) {
      axios
        .post('/api/v1/consolidation-cockpit/update/', {
          uuids: this.selectedInvoiceIds,
          action: action
        })
        .then(() => {
          toast({ message: 'Invoices updated', type: 'is-success' });
          this.selectedInvoiceIds = [];
          this.fetchInvoices();
        });
    },
  },
  watch: {
    'filters.opt_status'() {
      // reset to first page, then reload
      this.currentPage = 1;
      this.fetchInvoices();
    }
  },
  mounted() {
    this.fetchInvoices();
    this.getUniqueTypes();
    window.addEventListener('keydown', this.onGlobalKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onGlobalKeydown);
  },

};
</script>

<style scoped>
.table th,
.table td {
  text-align: left;
}
</style>